import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import "../MoodQuiz.css";

const smartMoods = [
  {
    id: "laughs",
    label: "Comedy",
    subtitle: "Feel-good & funny content",
    value: "laughs",
    color: "comedy"
  },
  {
    id: "romance", 
    label: "Romance",
    subtitle: "Love stories, drama & history",
    value: "romance",
    color: "romance"
  },
  {
    id: "action",
    label: "Action",
    subtitle: "High-energy, adventure & war", 
    value: "action",
    color: "action"
  },
  {
    id: "crime",
    label: "Crime & Mystery",
    subtitle: "Detective & investigation stories", 
    value: "crime",
    color: "crime"
  },
  {
    id: "horror",
    label: "Horror & Thriller",
    subtitle: "Scary & suspenseful content", 
    value: "horror",
    color: "horror"
  },
  {
    id: "documentary",
    label: "Real Stories",
    subtitle: "Documentary & biography", 
    value: "documentary",
    color: "documentary"
  }
];

const MoodQuiz = ({ onComplete, hasRecommendations = false, isLoading = false, quizCompleted = false }) => {
  const [selectedMood, setSelectedMood] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPersonality, setShowPersonality] = useState(false);

  const handleMoodSelect = (mood) => {
    // If recommendations are already showing and user changes mood, trigger immediate clear
    if (hasRecommendations && selectedMood && selectedMood.id !== mood.id) {
      // Call onComplete to trigger clearing of recommendations
      onComplete({ clearOnly: true });
    }
    
    setSelectedMood(mood);
    setShowPersonality(false);
    setSubmitting(false);
    
    // Show personality result briefly after selection
    setTimeout(() => {
      setShowPersonality(true);
    }, 300);
  };

  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => {
      onComplete({ mood: selectedMood.value });
      setSubmitting(false);
    }, 800);
  };

  const handleKeyDown = (event, mood) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMoodSelect(mood);
    }
  };


  // Get mood description based on selected mood
  const getMoodDescription = () => {
    if (!selectedMood) return null;
    
    const moodDescriptions = {
      laughs: "You love feel-good content that brings joy and laughter!",
      romance: "You enjoy heartwarming love stories, emotional dramas, and historical epics.",
      action: "You crave high-energy adventures, sci-fi thrills, and epic war stories!",
      crime: "You love investigative stories, mysteries, and criminal masterminds!",
      horror: "You crave spine-chilling scares and adrenaline-pumping suspense!",
      documentary: "You enjoy real stories, documentaries, and biographical content!"
    };
    
    return moodDescriptions[selectedMood.value] || "You have diverse tastes and enjoy various types of content!";
  };


  return (
    <div className="cinematic-panel-compact mb-3">
      <div className="panel-content-compact">
        <h6 className="panel-title-compact mb-3 text-center">
          <span role="img" aria-label="theater masks">🎭</span> What's your mood?
        </h6>
        
        {/* Always show mood grid */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mood-grid"
        >
          {smartMoods.map((mood, index) => (
                  <motion.div
                    key={mood.id}
                    className={`mood-card-badge ${mood.color} ${selectedMood?.id === mood.id ? 'selected' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleMoodSelect(mood)}
                    onKeyDown={(e) => handleKeyDown(e, mood)}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select ${mood.label} mood`}
                  >
                    <div className="mood-badge-header">
                      <h6 className="mood-badge-label">{mood.label}</h6>
                    </div>
                    <div className="mood-badge-content">
                      <p className="mood-badge-subtitle">{mood.subtitle}</p>
                    </div>
                  </motion.div>
          ))}
        </motion.div>

        {/* Results & Submit - show when mood is selected and no recommendations are showing, or when loading */}
        <AnimatePresence>
          {showPersonality && selectedMood && !hasRecommendations && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="quiz-result-compact mt-3"
            >
              <div className="result-compact text-center">
                <p className="mood-description-text">
                  {getMoodDescription()}
                </p>
                
                <Button
                  variant="primary"
                  disabled={submitting || isLoading}
                  onClick={handleSubmit}
                  className="submit-btn-compact"
                  size="lg"
                >
                  {submitting || isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Finding Perfect Matches...
                    </>
                  ) : (
                    <>
                      <span role="img" aria-label="sparkles">✨</span>
                      <span className="ms-2 me-2">Get My Recommendations</span>
                      <span role="img" aria-label="right arrow">🎬</span>
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

export default MoodQuiz;
